const AXEPTIO_SDK_URL_FRAGMENT = 'static.axept.io/sdk.js';

let primaryAxeptioScript: HTMLScriptElement | null = null;
let isGuardInstalled = false;

function isAxeptioSdkScript(node: Node): node is HTMLScriptElement {
  return node instanceof HTMLScriptElement && node.src.includes(AXEPTIO_SDK_URL_FRAGMENT);
}

function isAxeptioSdkLoaded(): boolean {
  return !!(window as any).axeptioSDK;
}

// GTM's injectScript waits for the script's load/error event, so the skipped duplicate
// must still report the outcome of the script that is actually loading the SDK.
function mirrorPrimaryScriptResult(duplicate: HTMLScriptElement): void {
  if (isAxeptioSdkLoaded()) {
    setTimeout(() => duplicate.dispatchEvent(new Event('load')));
    return;
  }

  const primary = primaryAxeptioScript;
  primary.addEventListener('load', () => duplicate.dispatchEvent(new Event('load')), { once: true });
  primary.addEventListener(
    'error',
    () => {
      if (primaryAxeptioScript === primary) {
        primaryAxeptioScript = null;
      }
      duplicate.dispatchEvent(new Event('error'));
    },
    { once: true }
  );
}

function shouldSkipInsertion(node: Node): node is HTMLScriptElement {
  if (!isAxeptioSdkScript(node)) {
    return false;
  }

  if (primaryAxeptioScript || isAxeptioSdkLoaded()) {
    return true;
  }

  primaryAxeptioScript = node;
  return false;
}

/**
 * The Axeptio SDK throws "Axeptio SDK is already loaded" when sdk.js is executed twice.
 * The GTM Axeptio tag re-injects sdk.js on every history change (SPA navigation),
 * so only the first insertion is allowed to reach the DOM.
 */
export function installAxeptioSdkGuard(): void {
  if (isGuardInstalled || typeof Node === 'undefined') {
    return;
  }
  isGuardInstalled = true;

  const originalInsertBefore = Node.prototype.insertBefore;
  const originalAppendChild = Node.prototype.appendChild;

  Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (shouldSkipInsertion(node)) {
      mirrorPrimaryScriptResult(node);
      return node;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };

  Node.prototype.appendChild = function <T extends Node>(this: Node, node: T): T {
    if (shouldSkipInsertion(node)) {
      mirrorPrimaryScriptResult(node);
      return node;
    }
    return originalAppendChild.call(this, node) as T;
  };
}
